import {Component, ElementRef, Input, OnInit, OnDestroy, ViewChild} from "@angular/core";
import {Router, RouterLink} from "@angular/router";
import {BreadcrumbItem} from "src/app/core/models/BreadcrumbItem";
import {Employee} from "src/app/core/models/Employee";
import {EmployeeService} from "src/app/core/services/services/employee.service";
import {DataService} from "../../../services/services/data.service";
import {EmployeeFullName} from "../../../models/EmployeeFullName";
import {UntypedFormControl, ReactiveFormsModule} from "@angular/forms";
import {filter, tap} from "rxjs/operators";
import {Observable} from "rxjs";
import Config from "../../../config/Config";
import {CustomizingService} from "../../../services/services/customizing.service";
import {AuthenticationService} from "../../../services/services/authentication.service";
import {AsyncPipe} from "@angular/common";
import {MatFormField, MatSuffix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {SanitizeInputDirective} from "src/app/shared/inputdirectives/sanitize-input.directive";
import {EmployeeAutocompleteComponent} from "src/app/shared/autocomplete/components/employee-autocomplete.component";
import {TranslatePipe} from "@ngx-translate/core";

@Component({
	selector: "app-breadcrumb-profile-search",
	templateUrl: "./breadcrumb-profile-search.component.html",
	styleUrls: ["./breadcrumb-profile-search.component.scss"],
	imports: [
		AsyncPipe,
		RouterLink,
		ReactiveFormsModule,
		MatFormField,
		MatInput,
		MatAutocompleteTrigger,
		MatIconButton,
		MatSuffix,
		MatIcon,
		SanitizeInputDirective,
		EmployeeAutocompleteComponent,
		TranslatePipe,
	],
})
export class BreadcrumbProfileSearchComponent implements OnInit, OnDestroy {
	@Input() breadcrumb: BreadcrumbItem;
	currentEmployeeObservable: Observable<Employee>;
	lastEmployee: Employee;
	employeeForm: UntypedFormControl = new UntypedFormControl();
	onlyOwnBU: boolean = false;
	isSearchVisible: boolean = false;
	readonly detailPath: string = "/my-profiles/";

	@ViewChild("employeeInput") employeeInput: ElementRef;
	@ViewChild(EmployeeAutocompleteComponent) employeeAutocomplete: EmployeeAutocompleteComponent;

	constructor(
		private _employeeService: EmployeeService,
		private _router: Router,
		private _dataService: DataService,
		private _customizingService: CustomizingService,
		private _authService: AuthenticationService,
	) {}

	ngOnInit() {
		this.onlyOwnBU = this._authService.isLineManager();
		this.isSearchVisible = this._authService.isLineManager() || this._authService.isMepAdmin();
		this.fetchCurrentEmployeeFromUrlParam();

		this.currentEmployeeObservable = this._dataService.currentEmployee.pipe(
			filter((e) => e !== undefined),
			tap((currentEmployee) => {
				this.updateCurrentEmployee(currentEmployee);
			}),
		);
	}

	private updateCurrentEmployee(currentEmployee: Employee) {
		this.lastEmployee = currentEmployee;
		this.updateCurrentEmployeeForm(currentEmployee);
		this.employeeAutocomplete?.clearOptions();
	}

	private fetchCurrentEmployeeFromUrlParam() {
		const subPath = this.breadcrumb.path.substring(
			this.breadcrumb.path.lastIndexOf(this.detailPath) + this.detailPath.length,
		);
		const id =
			subPath.lastIndexOf("/") > 0
				? Number.parseInt(subPath.substring(0, subPath.lastIndexOf("/")), 10)
				: Number.parseInt(subPath, 10);
		if (!Number.isNaN(id)) {
			this._customizingService.getCurrentClient().then((client) => {
				this._employeeService
					.getEmployee(Config.services.employees.baseUrl + id, client, null, false)
					.then((employee) => this._dataService.changeEmployee(employee));
			});
		}
	}

	updateCurrentEmployeeForm(employee: Employee): void {
		this.employeeForm.setValue(this.getEmployeeDisplayValue(employee), {emitEvent: false});
	}

	private getEmployeeDisplayValue(employee: Employee): string {
		if (!employee) {
			return "";
		}

		return employee.personnelNumber
			? `${employee.firstName} ${employee.lastName}, ${employee.personnelNumber}`
			: `${employee.firstName} ${employee.lastName}`;
	}

	onItemChange(employee: EmployeeFullName) {
		this._employeeService.getEmployeeByIdWithProfileAccess(employee.id).subscribe({
			next: (selectedEmployee) => {
				this._dataService.changeEmployee(selectedEmployee);
			},
			error: () => {
				/* 403 access denied - my-profiles component will handle display */
			},
		});

		this._router.navigate([this.detailPath + employee.id]);
	}

	onClear() {
		this.employeeForm.reset();
	}

	onLeaveText() {
		// deferred so a click on an autocomplete option (which blurs the input first) is processed before we reset the value
		setTimeout(() => {
			if (this.lastEmployee) {
				this.updateCurrentEmployeeForm(this.lastEmployee);
			}
		});
	}

	ngOnDestroy() {
		this.lastEmployee = null;
		this.employeeForm.reset();
		this._dataService.clearEmployeeCache();
	}
}
